import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    document.title = "Our Projects | Amar Preet Scaffolding";
    axios.get(`${API}/projects`)
      .then(res => {
        setProjects(res.data);
        setFilteredProjects(res.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleFilter = (filter) => {
    setActiveFilter(filter);
    if (filter === "all") {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.status === filter));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="projects-page">
      <SEO title="Our Projects" description="Explore 141+ completed scaffolding projects by Amar Preet. AIIMS, Tata Chemicals, Vegas Mall, Embassy Oxygen and more across India." canonical="/projects" />
      {/* Hero */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="projects-headline">
            Our Projects
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Trusted by leading organizations across India for large-scale construction projects
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">
            {[
              { id: "all", name: "All Projects" },
              { id: "ongoing", name: "Ongoing" },
              { id: "completed", name: "Completed" }
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => handleFilter(filter.id)}
                className={`px-5 py-2 rounded-full font-medium text-sm transition-all ${
                  activeFilter === filter.id
                    ? "bg-yellow-500 text-slate-900"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`filter-${filter.id}`}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white"
                data-testid={`project-${project.id}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={project.image}
                    alt={`${project.name} - ${project.category} scaffolding project in ${project.location}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {project.status === "ongoing" && (
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-orange-500 text-white">
                        Ongoing
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{project.name}</h3>
                  <div className="flex items-center text-slate-500 text-sm mb-1">
                    <MapPin className="w-3.5 h-3.5 mr-1" />
                    {project.location}
                  </div>
                  <p className="text-slate-400 text-sm">{project.category}</p>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-slate-600">No projects found for this filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Start Your Project With Us
          </h2>
          <p className="text-slate-700 mb-8 max-w-xl mx-auto">
            Join 200+ satisfied clients who trust Amar Preet for their scaffolding needs
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-all hover:scale-105"
            data-testid="projects-cta"
          >
            Get Project Quote
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
